import { diaDaSemana, diaLocal, somarDias, type DiaCivil, type PainelDaHome } from "@noesis/shared";
import { useSessao } from "../estado/sessao.js";
import { Icone } from "./Icone.js";
import { Visto } from "./Tracos.js";

/**
 * A fileira "Esta semana" do Caderno (Início 7a, fim de sessão 8d): seg a dom,
 * com visto verde no dia cumprido, x vermelho no dia que falhou, traço em
 * hoje ainda aberto e círculo tracejado no que não chegou ou é descanso.
 *
 * Lê o heatmap do painel da home, que é o diário de verdade: dia futuro nunca
 * aparece cumprido. Dia anterior à criação da conta é ponto neutro, nunca x
 * (ADR 041): ninguém falhou num dia em que ainda não estava aqui.
 */

const SEMANA = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];
const NOMES_LONGOS = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

export type EstadoDoDia = "feito" | "falhou" | "hoje" | "futuro" | "descanso" | "antes";

/** O dia em que a conta nasceu, no fuso do usuário (invariante 16). */
export function useInicioDaConta(): DiaCivil {
  const { usuario } = useSessao();
  return diaLocal(usuario.criadoEm, usuario.fusoHorario);
}

export function semanaAtual(
  p: PainelDaHome,
  inicioDaConta: DiaCivil | null = null,
): { rotulo: string; data: string; estado: EstadoDoDia }[] {
  const segunda = somarDias(p.hoje, -((diaDaSemana(p.hoje) + 6) % 7));
  const porData = new Map(p.heatmap.map((d) => [d.data, d]));
  const descanso = new Set(p.diasDeDescanso);
  return SEMANA.map((rotulo, i) => {
    const data = somarDias(segunda, i);
    const dia = porData.get(data);
    let estado: EstadoDoDia;
    if (data > p.hoje) estado = descanso.has(diaDaSemana(data)) ? "descanso" : "futuro";
    else if (inicioDaConta && data < inicioDaConta && !dia) estado = "antes";
    else if (dia?.cumpriu || (data === p.hoje && p.cumpriuHoje)) estado = "feito";
    else if (data === p.hoje) estado = "hoje";
    else if (dia?.ferias || descanso.has(diaDaSemana(data))) estado = "descanso";
    else estado = "falhou";
    return { rotulo, data, estado };
  });
}

/** "Sábado e domingo são descanso." — a frase dos dias de descanso da trilha. */
export function fraseDoDescanso(dias: readonly number[]): string | null {
  if (dias.length === 0) return null;
  const nomes = [...dias].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map((d) => NOMES_LONGOS[d]!);
  const lista = nomes.length === 1 ? nomes[0]! : `${nomes.slice(0, -1).join(", ")} e ${nomes.at(-1)}`;
  const frase = `${lista.charAt(0).toUpperCase()}${lista.slice(1)}`;
  return nomes.length === 1 ? `${frase} é descanso.` : `${frase} são descanso.`;
}

const NA_SEMANA = ["no domingo", "na segunda", "na terça", "na quarta", "na quinta", "na sexta", "no sábado"];

/**
 * "Sua conta começou hoje." — só na semana em que a conta nasceu, que é
 * quando a fileira tem pontos neutros e precisa dizer por quê.
 */
export function fraseDoComeco(p: PainelDaHome, inicioDaConta: DiaCivil): string | null {
  const segunda = somarDias(p.hoje, -((diaDaSemana(p.hoje) + 6) % 7));
  if (inicioDaConta < segunda || inicioDaConta > p.hoje) return null;
  if (inicioDaConta === p.hoje) return "Sua conta começou hoje.";
  return `Sua conta começou ${NA_SEMANA[diaDaSemana(inicioDaConta)]}.`;
}

const LEITURA: Record<EstadoDoDia, string> = {
  feito: "meta cumprida",
  falhou: "meta não cumprida",
  hoje: "hoje, ainda não",
  descanso: "descanso",
  futuro: "ainda não chegou",
  antes: "antes da conta existir",
};

export function Semana({ painel, animarHoje = false }: { painel: PainelDaHome; animarHoje?: boolean }) {
  const dias = semanaAtual(painel, useInicioDaConta());
  return (
    <ol className="semana">
      {dias.map((d) => {
        const hoje = d.data === painel.hoje;
        return (
          <li key={d.data} className={`semana__dia semana__dia--${d.estado}${hoje ? " semana__dia--atual" : ""}`}>
            <span className="t-anotacao-p semana__rotulo">{d.rotulo}</span>
            {d.estado === "feito" ? (
              <Visto className={hoje && animarHoje ? "semana__marca semana__marca--desenhar" : "semana__marca"} feito />
            ) : d.estado === "falhou" ? (
              <Icone glifo="x" papel="acao" className="semana__marca semana__marca--falhou" />
            ) : d.estado === "antes" ? (
              <Icone glifo="ponto" papel="navegacao" className="semana__marca semana__marca--antes" />
            ) : d.estado === "hoje" ? (
              <Icone glifo="menos" papel="navegacao" className="semana__marca semana__marca--hoje" />
            ) : (
              <Icone glifo="pendente" papel="navegacao" className="semana__marca semana__marca--vazio" />
            )}
            <span className="so-leitor">{LEITURA[d.estado]}</span>
          </li>
        );
      })}
    </ol>
  );
}
